import { SessionTimeout, SessionTimeoutConfig } from './SessionTimeoutUtil';
import axios from 'axios';
import { logger } from '../logger';
import { SESSION_TIMED_OUT_API } from '../constants';

type PendingUpdate = {
  type: 'timeout' | 'loginStatus' | 'onTimeout' | 'onUserActivity';
  value: number | boolean | (() => void);
};

class SessionTimeoutManager {
  private static instance: SessionTimeoutManager | null = null;
  private sessionTimeout: SessionTimeout | null = null;
  private isInitialized = false;
  private pendingUpdates: PendingUpdate[] = [];

  private constructor() {}

  static getInstance(): SessionTimeoutManager {
    if (!SessionTimeoutManager.instance) {
      SessionTimeoutManager.instance = new SessionTimeoutManager();
    }
    return SessionTimeoutManager.instance;
  }

  initialize(config: SessionTimeoutConfig): void {
    if (this.isInitialized) return;
    if (typeof window === 'undefined') return;

    this.sessionTimeout = new SessionTimeout({
      isLoggedIn: config.isLoggedIn,
      submitActionLogin: config.submitActionLogin,
      submitActionAnonymous: config.submitActionAnonymous,
      onTimeout: async () => {
        await this.handleSessionTimedOut(SESSION_TIMED_OUT_API);
        config.onTimeout?.();
      },
      onUserActivity: config.onUserActivity,
      timeoutMs: config.timeoutMs,
    });

    this.sessionTimeout.initialize();
    this.isInitialized = true;

    // Process any pending updates that were queued before initialization
    this.processPendingUpdates();
  }

  private async handleSessionTimedOut(apiUrl: string, returnUrl?: string): Promise<void> {
    try {
      const response = await axios.post(
        apiUrl,
        {
          returnTo: returnUrl,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data?.redirect) {
        window.location.href = response.data.nextStateURI;
      }
    } catch (error) {
      const { log } = logger({})('SessionTimedOut');
      log.error('Session Timed Out failed', error);
    }
  }

  private processPendingUpdates(): void {
    if (!this.sessionTimeout || this.pendingUpdates.length === 0) return;

    this.pendingUpdates.forEach(update => {
      switch (update.type) {
        case 'timeout':
          this.sessionTimeout!.updateTimeout(update.value as number);
          break;
        case 'loginStatus':
          this.sessionTimeout!.updateLoginStatus(update.value as boolean);
          break;
        case 'onTimeout':
          this.sessionTimeout!.onTimeout = update.value as () => void;
          break;
        case 'onUserActivity':
          this.sessionTimeout!.onUserActivity = update.value as () => void;
          break;
      }
    });

    // Clear the queue
    this.pendingUpdates = [];
  }

  updateTimeout(timeoutMs: number): void {
    if (!this.sessionTimeout) {
      // Queue the update to be applied after initialization
      this.pendingUpdates.push({ type: 'timeout', value: timeoutMs });
      return;
    }
    this.sessionTimeout.updateTimeout(timeoutMs);
  }

  updateLoginStatus(isLoggedIn: boolean): void {
    if (!this.sessionTimeout) {
      // Queue the update to be applied after initialization
      this.pendingUpdates.push({ type: 'loginStatus', value: isLoggedIn });
      return;
    }
    this.sessionTimeout.updateLoginStatus(isLoggedIn);
  }

  updateOnTimeout(callback: () => void): void {
    if (!this.sessionTimeout) {
      // Queue the update to be applied after initialization
      this.pendingUpdates.push({ type: 'onTimeout', value: callback });
      return;
    }
    this.sessionTimeout.onTimeout = callback;
  }

  updateOnUserActivity(callback: () => void): void {
    if (!this.sessionTimeout) {
      // Queue the update to be applied after initialization
      this.pendingUpdates.push({ type: 'onUserActivity', value: callback });
      return;
    }
    this.sessionTimeout.onUserActivity = callback;
  }

  triggerTimeout(): void {
    this.sessionTimeout?.timeoutRedirect();
  }

  restartTimer(): void {
    this.sessionTimeout?.startTimer();
  }

  cleanup(): void {
    this.sessionTimeout?.cleanup();
    this.sessionTimeout = null;
    this.isInitialized = false;
  }

  getIsInitialized(): boolean {
    return this.isInitialized;
  }

  getCurrentTimeout(): number | null {
    return this.sessionTimeout?.expired ?? null;
  }

  getCurrentLoginStatus(): boolean {
    return this.sessionTimeout?.isLoggedIn ?? false;
  }
}

export const sessionTimeoutManager = SessionTimeoutManager.getInstance();
